import React, { useEffect, useState } from "react";
import { CeoNav } from "../components/CeoNav";
import { api, formatErrorDetail } from "../lib/api";
import { CardSkeleton, EmptyState, ErrorState } from "../components/LoadState";
import { Plus, Pencil, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

export default function StaffPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/staff");
      setItems(data);
    } catch (e) {
      const msg = e.userMessage || "Failed to load staff.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setName(""); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setName(s.name); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/staff/${editing.id}`, { name });
        toast.success("Staff updated");
      } else {
        await api.post("/staff", { name });
        toast.success("Staff added");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.userMessage || formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this staff?")) return;
    try { await api.delete(`/staff/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.userMessage || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-page">
      <CeoNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold">Staff</h1>
            <div className="text-sm text-muted-foreground">Non-login workers who participate in jobs</div>
          </div>
          <button
            data-testid="staff-new-btn"
            onClick={openNew}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 font-semibold text-sm"
          >
            <Plus size={16} /> Add staff
          </button>
        </div>

        {error ? <div className="mb-5"><ErrorState message={error} onRetry={load} /></div> : null}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {loading ? <CardSkeleton rows={3} /> : null}
          {!loading && items.length === 0 ? (
            <div className="md:col-span-2 lg:col-span-3">
              <EmptyState title="No staff yet" message="Add staff members before recording entries." />
            </div>
          ) : null}
          {!loading && items.map((s) => (
            <div key={s.id} className="card-soft p-4 flex items-center gap-3" data-testid={`staff-${s.id}`}>
              <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                <UserRound size={18} />
              </div>
              <div className="flex-1 font-semibold">{s.name}</div>
              <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-secondary" data-testid={`staff-edit-${s.id}`}><Pencil size={14} /></button>
              <button onClick={() => remove(s.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`staff-del-${s.id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </main>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="staff-dialog">
          <DialogTitle>{editing ? "Edit staff" : "Add staff"}</DialogTitle>
          <div className="mt-3">
            <label className="text-sm font-semibold mb-1.5 block">Full name</label>
            <input
              data-testid="staff-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-secondary text-sm font-semibold">Cancel</button>
            <button onClick={save} data-testid="staff-save" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110">Save</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
